export interface ReleaseNote {
    version: string;
    date: string;
    changes: string[];
}

export interface ReleaseMonth {
    month: string;
    notes: ReleaseNote[];
}

export const releaseNotes: ReleaseNote[] = [
    {
        version: 'web/api: 1.17.0/1.16.0',
        date: '2026-11-03',
        changes: [
            'Implemented partial commit and revert in pending changes.',
            'Fixed DBC reports not escaping commas in values.',
            'Fixed DBC reports duplicate receivers issue.',
            'Fixed DBC reports writing an empty attribute when a signal has no initial value.',
            'Added color coding to MSC messages by segment, with invalid messages highlighted.',
            'Fixed MSC validation for DW and Env messages.',
            'Enabled inserting and rearranging objects in MSC.',
            'Added right-click menu in Change Request summary list to open MSC.',
            'Fixed AED duplication being blocked by the Firewall restrictions when the request body exceeds 8 KB.',
            'Fixed reverting pending changes when deleting AE boxes connected to newly added FV boxes.',
            'Fixed FV box endpoint changes in AED so connections and AE details refresh correctly.',
            'Fixed SOP date in structure release report.',
        ],
    },
    {
        version: 'web/api: 1.16.0/1.15.0',
        date: '2026-10-23',
        changes: [
            'Added workspace search and validation to prevent duplicate or overlong workspace names.',
            'Added guidance and an access request link when a request is forbidden due to missing SESAMMTool access.',
            'Enabled large diagram rendering.',
            'Fixed CRUD validator for component-segment relations.',
            'Fixed an intermittent error when importing a DBC file after a successful preview.',
        ],
    },
    {
        version: 'web/api: 1.15.0/1.14.0',
        date: '2026-10-19',
        changes: [
            'Implemented CAN FD for segments',
            'Implemented Primary Address in DBC files. If Primary address is not available, fall back to the guessed address.',
            'Fixed AED duplicate FV Boxes bug.',
            'Fixed "Clone Signal" so that value table is copied.',
            'Added DBC Import feature to reports page.',
            'Fixed an intermittent error when importing a DBC file after a successful preview.',
            'Implemented CAN specification changes page and filter.',
            'Fixed MSC saves being blocked by the Firewall restrictions when the request body exceeds 8 KB.',
            'Added value description integer range restriction.',
        ],
    },
    {
        version: 'web/api: 1.14.0/1.13.0',
        date: '2026-10-13',
        changes: [
            'Added personal favorites feature, where most used pages can be added as favorites and be accessed, deleted or recolored in the home page.',
            'Added Allocation Element ID suggestion on creation.',
            'Fixed an issue in Segment reports where chosen objects disappeared from the selection when searching for another segment. They now remain selected and visible at the top of the list.',
            'Improved the behavior of CRUD name field validation for relevant exported objects.',
            'Changed error message display so it stays until user closes the toast.',
            'Fixed duplicate diagram FV activation bug.',
            'Fixed CAN specification document typo.',
        ],
    },

    {
        version: 'web/api: 1.13.0/1.12.0',
        date: '2026-10-07',
        changes: [
            'Added "OAS FPC" and "Comment" columns in Scenario Summary List table and export.',
            'Fixed MSC validation incorrectly marking components as invalid by matching component codes and component families across old and new versions.',
            'Added primary address field for components.',
            'Fixed bug in Message Signal Allocation view where signals could not be added in multiplexed groups.',
        ],
    },
    {
        version: 'web/api: 1.12.0/1.11.0',
        date: '2026-10-02',
        changes: [
            'Added sorting for Summary List.',
            'Added options to generate all DBC reports by segment.',
            'Added "_All" in DBC by segment filenames.',
            'Added consistent format validation to name field in CRUD forms.',
            'Fixed CRUD edited comment not update bug.',
            'Fixed address-based DBC node generation consistently across the Internal and Public APIs, ensuring all referenced nodes are declared with their correct CAN addresses.',
            'Fixed some cases where sending MSC for Review would cause an error.',
            'Fixed DBC messages "_FF" suffix.',
            'Added GW_ to addresses/nodes that are not directly tansmitting on the segment in the DBC report',
            'Prevented deletion of AE boxes with connected FV boxes in AED.',
        ],
    },
    {
        version: 'web/api: 1.11.0/1.10.0',
        date: '2026-09-24',
        changes: [
            'Added option to include unused signals in DBC files.',
            'Improved navigation tree sorting orders.',
            'Fixed FV activation summaries to resolve Tx/Rx activation using SOP precedence.',
            'Displaying the corresponding SOP and change request in activation summary.',
            'Fixed CAN specification signal tables to show units in the Unit column and numbered footnote references in the Note column.',
            'Fixed CAN specification message tables to show DPext, DP, Group Extension, destination, and Standard/Proprietary values.',
            'Added toggle to generate DBC files with Address Based Nodes.',
        ],
    },
    {
        version: 'web/api: 1.10.0/1.9.0',
        date: '2026-09-15',
        changes: [
            'Added watermark for MSC drafts.',
            'Made delete workspace optional and keep current CR when committing all changes.',
            'Fixed bug with saving of MSC files',
            'Preview feature, for reviewers only - possibility to upload MSC files with Draft status',
            'Fixed gateway path changes in Allocation Element Diagrams not being persisted after a successful save.',
        ],
    },
    {
        version: 'web/api: 1.9.0/1.8.0',
        date: '2026-09-09',
        changes: [
            "Added User Function editing & further updated 'Duplicate Diagram' functionality in Allocation Element Diagrams.",
            'Added DBC by Segment functionality.',
            'Fixed an issue where changes committed later for an older SOP could incorrectly override activation states from newer SOPs. Some inconsistencies are still present, further investigation ongoing.',
            'Fixed DBC generation to omit the global destination address suffix (_FF) from message names, matching ST2 behavior for both component- and segment-based generation.',
        ],
    },
    {
        version: 'web/api: 1.8.0/1.7.0',
        date: '2026-09-04',
        changes: [
            'Improved MSC validation accuracy and instance-label rendering. Validation of MSC now only works while using the validation button in the diagram toolbar.',
            'Implemented duplicate Allocation Element Diagram.',
            'Added endpoints for producing DBC file for component-segments and for retrieval of components-segments relations to Public API.',
            'Fixed global search to support special characters (( . ), ( , ), ( _ ), ( - ), ( / )).',
            'Fixed DBC CAN ID so that signals starting with digits have underscore prefix.',
            'Fixed tree view search and listing names to respect SOP-effective anchor names after baseline/versioned name changes.',
            'Fixed MSC parser handling of message arguments containing nested parentheses, which could cause the parsing to break.',
            'Implemented email notifications to MSC coordinators when an MSC is ready for review.',
            'Added options to generate all CAN spec and DBC reports.',
        ],
    },
    {
        version: 'web/api: 1.7.0/1.6.0',
        date: '2026-08-27',
        changes: [
            'Fixed DBC multiplexor indicator and value bug.',
            'Fixed Message Signal Allocation multiplexor value display.',
            'Fixed bug with access to saving of MSC diagram.',
            'Fixed bug with editing user functions.',
            'Implemented Scania Lexicon Integration.',
        ],
    },
    {
        version: 'web/api: 1.6.0/1.5.0',
        date: '2026-08-21',
        changes: [
            'Fixed DBC missing messages bug.',
            'Fixed bug with the out of memory exception when exporting CAN Specification reports.',
            'Fixed the CAN Specification report ignoring the selected SOP, which could produce incorrect CAN identifiers.',
            'Implemented duplicate FV Boxes in AED.',
        ],
    },
    {
        version: 'web/api: 1.5.0/1.4.0',
        date: '2026-08-14',
        changes: [
            'Added a context menu button to clone Signals.',
            'Fixed a bug which, sometimes, adding a higher amount of signals at once could cause errors.',
            'Fixed baseline changes being incorrectly compared against future SOP versions.',
        ],
    },
    {
        version: 'web/api: 1.4.0/1.3.0',
        date: '2026-08-07',
        changes: [
            'Added Change Request Details dialog, available from the navigation tree right-click menu.',
            'Added column filters for Summary List.',
            'Added dev environment differentiation using banner, watermark, and alternative color theme.',
            'Added yellow highlight to searched terms for Communication Table, Summary List, Navigation Tree, and Reports.',
            'Added export button for dynamic and static diagrams (PNG and SVG).',
            'Implemented duplicate AE boxes in AED.',
            'Fixed a misleading save error that could appear even when an AED was successfully saved.',
            'Fixed a display issue where engine addresses with value 0 appeared blank in the address summary list.',
            'Fixed an issue where details and version history could show baseline data instead of the selected SOP changes, making records like PBC2 appear inactive or outdated incorrectly.',
            'Fixed Create/Edit fields so that OasFpc is not mandatory and Formal Condition is mandatory',
            'Fixed time display so that the frontend shows user local time.',
            'Improved dropdown search by clearing the search text when the modal closes, searching while typing, and supporting AE search in the FvBox creation dropdown.',
        ],
    },
    {
        version: 'web/api: 1.3.0/1.2.0',
        date: '2026-07-20',
        changes: [
            'Fixed Pending Changes to show the correct SSCR in the Change Request column when switching between SSCRs before committing.',
            'Fixed PGN input to accept 5-digit hex values (0x00000-0x3FFFF) instead of 4.',
            'Fixed a problem where saving larger FVs with many signals could fail in production due to firewall restrictions.',
            'Limited workspace name to 30 characters in the Workspace Dialog.',
            'AE boxes in AED now crop text that exceeds element boundaries, matching the existing FV text overflow behavior.',
        ],
    },
    {
        version: 'web & api: 1.2.0',
        date: '2026-07-10',
        changes: [
            'Added "Change FV activation dialog" in AED.',
            'Merged FV boxes with same communication but different activation status in diagrams (as in ST2).',
        ],
    },
];

const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export function getReleaseMonths(notes: ReleaseNote[] = releaseNotes): ReleaseMonth[] {
    const groupedNotes = new Map<string, ReleaseNote[]>();
    const sortedNotes = [...notes].sort((left, right) => right.date.localeCompare(left.date));

    for (const note of sortedNotes) {
        const month = getReleaseNoteMonth(note.date);
        const notesForMonth = groupedNotes.get(month);

        if (notesForMonth) {
            notesForMonth.push(note);
        } else {
            groupedNotes.set(month, [note]);
        }
    }

    return Array.from(groupedNotes, ([month, notesForMonth]) => ({
        month,
        notes: notesForMonth,
    }));
}

function getReleaseNoteMonth(date: string): string {
    const [year, month] = date.split('-');
    const monthIndex = Number(month) - 1;
    return `${monthNames[monthIndex]} ${year}`;
}
